/**
 * Reglas antilavado de la Ley contra el Lavado de Dinero u Otros Activos
 * (Decreto 67-2001) y su reglamento (Acuerdo Gubernativo 118-2002),
 * supervisadas por la IVE (Intendencia de Verificación Especial).
 *
 * Las personas obligadas deben registrar las transacciones en efectivo de
 * US$10,000 o más (o su equivalente en quetzales) — individuales o
 * acumuladas por la misma persona en un día — y reportar las transacciones
 * sospechosas (inusuales).
 */
import { keyedEnum } from '../catalogs/enum.js';
import { ALERT_SEVERITY } from '../catalogs/operations.js';
import { formatMoney } from '../money/format.js';
/** Umbral del Registro de Transacciones en Efectivo (RTE), en dólares. */
export const CASH_TRANSACTION_REPORT_THRESHOLD_USD = 10_000;
/** Umbral en centavos de quetzal dado el tipo de cambio en centavos por US$ (p. ej. 772 = Q7.72). */
export function cashReportThreshold(exchangeRateCentsPerUsd) {
    return (CASH_TRANSACTION_REPORT_THRESHOLD_USD * exchangeRateCentsPerUsd);
}
/**
 * Fracción mínima del umbral que debe tener un pago para contarlo como posible
 * fraccionamiento (20 %): evita alertar por cuotas pequeñas y rutinarias.
 */
export const STRUCTURING_MIN_PAYMENT_FRACTION = 0.2;
/**
 * Ventana (en días) en que se acumula el efectivo del cliente para detectar
 * fraccionamiento.
 */
export const STRUCTURING_WINDOW_DAYS = 30;
/**
 * Un pago de este número de cuotas regulares o más se considera un prepago
 * inusual frente al perfil del cliente.
 */
export const UNUSUAL_PREPAYMENT_INSTALLMENTS = 5;
/** Tipos de alerta de cumplimiento. */
export const COMPLIANCE_ALERT_TYPES = [
    'cash_threshold',
    'structuring',
    'unusual_prepayment',
    'pep_transaction',
    'third_party_payment',
];
/** Catálogo con clave de tipos de alerta: `COMPLIANCE_ALERT_TYPE.structuring`. */
export const COMPLIANCE_ALERT_TYPE = keyedEnum(COMPLIANCE_ALERT_TYPES);
/** Etiquetas en español de cada tipo de alerta. */
export const COMPLIANCE_ALERT_LABELS = {
    cash_threshold: 'Transacción en efectivo sobre el umbral (RTE)',
    structuring: 'Posible fraccionamiento de efectivo',
    unusual_prepayment: 'Pago anticipado inusual vs. perfil',
    pep_transaction: 'Transacción de Persona Expuesta Políticamente',
    third_party_payment: 'Pago realizado por un tercero',
};
/**
 * Tamiza un pago y devuelve las alertas que debe revisar el oficial de
 * cumplimiento. Nunca bloquea el pago: la ley exige registrar y reportar, no
 * rechazar (rechazarlo podría alertar al cliente).
 */
export function screenPayment(input) {
    const alerts = [];
    const threshold = cashReportThreshold(input.exchangeRateCentsPerUsd);
    if (input.isCash) {
        const dayTotal = input.clientCashToday + input.amount;
        if (dayTotal >= threshold) {
            alerts.push({
                type: COMPLIANCE_ALERT_TYPE.cash_threshold,
                severity: ALERT_SEVERITY.critical,
                description: `Efectivo del día ${formatMoney(dayTotal)} ≥ umbral ${formatMoney(threshold)}. Registrar en el RTE.`,
            });
        }
        else {
            // Varios pagos en efectivo, cada uno bajo el umbral, que juntos lo superan en 30 días.
            const monthTotal = input.clientCashLast30Days + input.amount;
            if (monthTotal >= threshold && input.amount >= threshold * STRUCTURING_MIN_PAYMENT_FRACTION) {
                alerts.push({
                    type: COMPLIANCE_ALERT_TYPE.structuring,
                    severity: ALERT_SEVERITY.warning,
                    description: `Efectivo acumulado en ${String(STRUCTURING_WINDOW_DAYS)} días ${formatMoney(monthTotal)} supera el umbral mediante pagos menores.`,
                });
            }
        }
    }
    if (input.regularInstallment > 0 && input.amount >= input.regularInstallment * UNUSUAL_PREPAYMENT_INSTALLMENTS) {
        alerts.push({
            type: COMPLIANCE_ALERT_TYPE.unusual_prepayment,
            severity: ALERT_SEVERITY.warning,
            description: `Pago de ${formatMoney(input.amount)} equivale a más de ${String(UNUSUAL_PREPAYMENT_INSTALLMENTS)} cuotas regulares; verificar origen de fondos.`,
        });
    }
    if (input.clientIsPep) {
        alerts.push({
            type: COMPLIANCE_ALERT_TYPE.pep_transaction,
            severity: ALERT_SEVERITY.info,
            description: 'Cliente PEP: aplicar debida diligencia ampliada.',
        });
    }
    if (input.paidByThirdParty) {
        alerts.push({
            type: COMPLIANCE_ALERT_TYPE.third_party_payment,
            severity: ALERT_SEVERITY.info,
            description: 'El pago lo realizó una persona distinta al titular; conservar identificación del tercero.',
        });
    }
    return alerts;
}
//# sourceMappingURL=aml.js.map